import { useAuth0 } from '@auth0/auth0-react'
import {
  Label,
  ProductSelector,
  ProductSelectorOption,
  ProductSelectorOptionProps,
} from '@dfh-corp-sites/ui'
import React, { FC, useEffect } from 'react'
import { Controller, UseFormMethods } from 'react-hook-form'
import { useId } from 'react-id-generator'

import { ConfigurableProductDetailsFragment } from '../../../graphql/magento'
import { ProductLabelStatus } from '../../../graphql/search'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { useDispensaryContext } from '../../../lib/dispensaries'
import {
  AttributeName,
  AttributeSkuMapping,
  AttributeValue,
  FormattedVariantData,
} from '../../../utils/itemTools'
import LabelStatusIcon from '../../LabelStatusIcon'
import ItemSelectionIndicatorMessage from './ItemSelectionIndicatorMessage'
import { ItemSelectorOptionPrice } from './ItemSelectorOptionLabel'

export interface ItemVariantOptionsProps {
  item: ConfigurableProductDetailsFragment
  hasSelectedVariantCombination: boolean
  onVariantSelected: (itemValue: string) => void
  control: UseFormMethods['control']
  variantData: FormattedVariantData
  variantSelections: Record<AttributeName, AttributeValue>
  variantLabelStatus: ProductLabelStatus | undefined
  size?: ProductSelectorOptionProps['size']
}

const ItemVariantOptionsAttribute: FC<
  Pick<ItemVariantOptionsProps, 'item' | 'control' | 'variantData' | 'size'> & {
    showPrice: boolean
    attribute: string
    index: number
    variantSelections: Record<AttributeName, AttributeValue>
  }
> = ({ item, control, variantData, showPrice, attribute, index, variantSelections, size }) => {
  const attributeValues = variantData.attributeValues[attribute]
  const ids = useId(attributeValues.length, `${attribute}-`)

  const parentAttribute = variantData.usedAttributes[index - 1]

  const isInvalidCombination = (value: string) => {
    return (
      parentAttribute &&
      variantSelections[parentAttribute] &&
      !Object.values(variantData.productIndex).some((productIndex) => {
        return (
          productIndex[parentAttribute] === variantSelections[parentAttribute] &&
          productIndex[attribute] === value
        )
      })
    )
  }
  const labelId = `${attribute}-label`
  return (
    <div className="space-y-3">
      <Label className="font-medium" id={labelId}>
        Choose a {attribute}
      </Label>
      <Controller
        control={control}
        name={`variant.${attribute}`}
        render={(field) => (
          <ProductSelector
            id={`variant.${attribute}`}
            name={field.name}
            defaultValue={field.value}
            onValueChange={field.onChange}
            aria-labelledby={labelId}
          >
            {attributeValues.map((value, valueIndex) => {
              const isDisabled = isInvalidCombination(value)
              const isChecked = field.value === value

              if (isChecked && isDisabled) {
                const firstAvailableOption =
                  Object.values(variantData.productIndex).find((productIndex) => {
                    return productIndex[parentAttribute] === variantSelections[parentAttribute]
                  })?.[attribute] ?? value
                field.onChange(firstAvailableOption)
              }

              return (
                <ProductSelectorOption
                  size={size}
                  checked={isChecked}
                  disabled={Boolean(isDisabled)}
                  key={value}
                  id={`${ids[valueIndex]}-${value}`}
                  value={value}
                  label={value}
                  price={
                    showPrice &&
                    index === 0 && (
                      <ItemSelectorOptionPrice
                        item={item}
                        skus={variantData.attributeProductList[attribute]?.[value] ?? []}
                      />
                    )
                  }
                />
              )
            })}
          </ProductSelector>
        )}
      />
    </div>
  )
}

const ItemVariantOptions: FC<ItemVariantOptionsProps> = ({
  item,
  hasSelectedVariantCombination,
  onVariantSelected,
  control,
  variantData,
  variantSelections,
  variantLabelStatus,
  size,
}) => {
  const { usedAttributes } = variantData
  const { featureFlags } = useSiteMetadata() ?? {}
  const { isAuthenticated } = useAuth0()
  const dispensary = useDispensaryContext()
  const anonymousCart = featureFlags?.anonymousCart ?? false
  const showPrice = isAuthenticated || !!dispensary || anonymousCart

  useEffect(() => {
    if (hasSelectedVariantCombination) {
      onVariantSelected(
        // TODO: add check that nested navigation/reduction actually ends up with a leaf/string
        usedAttributes.reduce(
          (check, att) => check[variantSelections[att]] as AttributeSkuMapping,
          variantData.mapping,
        ) as unknown as string,
      )
    }
  }, [
    hasSelectedVariantCombination,
    onVariantSelected,
    usedAttributes,
    variantData.mapping,
    variantSelections,
  ])

  return (
    <>
      {usedAttributes?.map((attribute, index) => (
        <ItemVariantOptionsAttribute
          key={attribute}
          item={item}
          control={control}
          variantData={variantData}
          showPrice={showPrice}
          attribute={attribute}
          index={index}
          variantSelections={variantSelections}
          size={size}
        />
      ))}
      {variantLabelStatus && (
        <ItemSelectionIndicatorMessage
          message={
            variantLabelStatus === ProductLabelStatus.Present
              ? 'LABEL ON FILE'
              : 'LABEL NOT ON FILE'
          }
        >
          <LabelStatusIcon isLabelPresent={variantLabelStatus === ProductLabelStatus.Present} />
        </ItemSelectionIndicatorMessage>
      )}
    </>
  )
}

export default ItemVariantOptions
